// Sentences from tool results, for when the model's own sentence cannot be trusted.
import type { Finding } from '../build';
import type { ToolCall } from '../types';
import { typeWords } from '../tools';

export function queryText(out: Record<string, unknown>, de: boolean): string {
  const count = Number(out.count ?? 0);
  const parts: string[] = [];
  const agg = out.aggregate as
    { op: string; field: string; value: number | null; missing?: number } | undefined;
  if (agg && agg.value != null) {
    parts.push(
      de
        ? `${agg.op}(${agg.field}) = ${fmt(agg.value, de)} über ${count} Elemente${agg.missing ? ` (${agg.missing} ohne Wert)` : ''}.`
        : `${agg.op}(${agg.field}) = ${fmt(agg.value, de)} across ${count} elements${agg.missing ? ` (${agg.missing} without a value)` : ''}.`,
    );
  } else parts.push(de ? `${count} Elemente.` : `${count} elements.`);
  const groups = out.groups as Record<string, number | null> | undefined;
  if (groups) {
    const rows = Object.entries(groups)
      .slice(0, 8)
      .map(([k, v]) => `${k}: ${v == null ? '–' : fmt(v, de)}`)
      .join(', ');
    parts.push((de ? `Nach ${out.group_by}: ` : `By ${out.group_by}: `) + rows + '.');
  }
  const els = out.elements as Array<Record<string, unknown>> | undefined;
  if (els?.length && !groups) {
    const rows = els
      .slice(0, 5)
      .map((e) => {
        const extras = Object.entries(e)
          .filter(([k]) => !['id', 'type', 'name', 'storey', 'material'].includes(k))
          .map(([k, v]) => `${k} ${typeof v === 'number' ? fmt(v, de) : String(v ?? '–')}`)
          .join(', ');
        return `${String(e.name)} (${typeWords(String(e.type))[de ? 'de' : 'en']}, ${String(e.storey ?? '–')}${extras ? ', ' + extras : ''})`;
      })
      .join('; ');
    parts.push((de ? 'Zum Beispiel: ' : 'For example: ') + rows + '.');
  }
  parts.push(de ? 'Markiert.' : 'Highlighted.');
  return parts.join(' ');
}

export function describeText(out: Record<string, unknown>, de: boolean): string {
  const list = (k: string) =>
    ((out[k] as Array<{ name: string; count: number }> | undefined) ?? [])
      .slice(0, 8)
      .map((x) => `${x.name} (${x.count})`)
      .join(', ');
  return de
    ? `${out.elements} Elemente. Typen: ${list('types')}. Materialien: ${list('materials')}. Geschosse: ${list('storeys')}.`
    : `${out.elements} elements. Types: ${list('types')}. Materials: ${list('materials')}. Storeys: ${list('storeys')}.`;
}

export function renderLast(calls: ToolCall[], de: boolean): string | null {
  for (let i = calls.length - 1; i >= 0; i--) {
    const c = calls[i]!;
    const out = c.output as Record<string, unknown>;
    if (!out || typeof out !== 'object' || 'error' in out) continue;
    if (c.tool === 'query_elements') return queryText(out, de);
    if (c.tool === 'describe_model') return describeText(out, de);
    if (c.tool === 'count_elements')
      return de ? `${out.count} Elemente. Markiert.` : `${out.count} elements. Highlighted.`;
    if (c.tool === 'find_elements' || c.tool === 'elements_near') return queryText(out, de);
    if (c.tool === 'audit_model') return auditText(out, de);
    if (c.tool === 'scene') return sceneText(out, de);
    if (c.tool === 'build') return buildText(out, de);
    if (c.tool === 'check_building') return checkText(out, de);
    if (c.tool === 'export_takeoff') return exportText(out, de);
  }
  return null;
}

const fmt = (v: number, de: boolean) =>
  (Math.abs(v) >= 100 ? Math.round(v) : Number(v.toFixed(2))).toLocaleString(
    de ? 'de-AT' : 'en-GB',
  );

export function auditText(out: Record<string, unknown>, de: boolean): string {
  const issues = Object.entries((out.issues as Record<string, number>) ?? {}).filter(
    ([, n]) => n > 0,
  );
  if (!issues.length)
    return de
      ? `Keine Auffälligkeiten in ${out.elements} Elementen.`
      : `No issues in ${out.elements} elements.`;
  const rows = issues.map(([k, n]) => `${k.replace(/_/g, ' ')}: ${n}`).join(', ');
  return de
    ? `${out.elements} Elemente geprüft. ${rows}. Elemente ohne Material sind markiert.`
    : `${out.elements} elements checked. ${rows}. Elements without material are highlighted.`;
}

export function sceneText(out: Record<string, unknown>, de: boolean): string {
  const e = out.effect as { kind: string } | undefined;
  if (!e) return de ? 'Nichts zu zeigen.' : 'Nothing to show.';
  const n = Number(out.count ?? 0);
  switch (e.kind) {
    case 'focus':
      return de ? `Kamera auf ${n} Elemente gerichtet.` : `Camera on ${n} elements.`;
    case 'isolate':
      return de
        ? `Nur diese ${n} Elemente sind sichtbar.`
        : `Only these ${n} elements are visible.`;
    case 'section':
      return de ? `Schnitt bei ${out.label}.` : `Section at ${out.label}.`;
    case 'colour': {
      const groups = ((out.groups as Array<{ key?: string; range?: string; count: number }>) ?? [])
        .slice(0, 8)
        .map((g) => `${g.key ?? g.range} (${g.count})`)
        .join(', ');
      const rest = Number(out.unassigned ?? 0);
      return (
        (de ? `Eingefärbt nach ${out.field}: ` : `Coloured by ${out.field}: `) +
        groups +
        (rest
          ? de
            ? `; ${rest} ohne Wert bleiben grau.`
            : `; ${rest} without a value stay grey.`
          : '.')
      );
    }
    case 'label':
      return de ? `${n} Elemente beschriftet.` : `${n} elements labelled.`;
    default:
      return de ? 'Ansicht zurückgesetzt.' : 'View reset.';
  }
}

export function buildText(out: Record<string, unknown>, de: boolean): string {
  if (out.bytes instanceof Uint8Array)
    return de
      ? `IFC exportiert, ${out.bytes.length} Bytes.`
      : `IFC exported, ${out.bytes.length} bytes.`;
  const lang = de ? 'de' : 'en';
  const created = (out.created as { type: string; storey: string | null }[] | undefined) ?? [];
  const removed = ((out.removed as unknown[] | undefined) ?? []).length;
  const findings = (out.findings as Finding[] | undefined) ?? [];
  const parts: string[] = [];
  if (created.length) {
    const storey = created.find((e) => e.storey)?.storey;
    const counts = new Map<string, number>();
    for (const e of created) counts.set(e.type, (counts.get(e.type) ?? 0) + 1);
    const by = [...counts.entries()].map(([t, n]) => `${n} ${typeWords(t)[lang]}`).join(', ');
    parts.push(
      de
        ? `${storey ? storey + ': ' : ''}${by} hinzugefügt.`
        : `${storey ? storey + ': ' : ''}${by} added.`,
    );
  } else if (removed) {
    parts.push(de ? `${removed} Elemente entfernt.` : `${removed} elements removed.`);
  } else if (typeof out.note === 'string') {
    parts.push(out.note);
  }
  if (findings.length)
    parts.push(
      (de ? `${findings.length} Befunde: ` : `${findings.length} findings: `) +
        findingsText(findings, de),
    );
  return parts.join(' ') || (de ? 'Erledigt.' : 'Done.');
}

export function exportText(out: Record<string, unknown>, de: boolean): string {
  const name = (out.attachment as { name?: string } | undefined)?.name ?? '–';
  const sheets = ((out.sheets as { name: string }[] | undefined) ?? [])
    .map((s) => (de ? (SHEET_DE[s.name] ?? s.name) : s.name.toLowerCase()))
    .join(', ');
  return de
    ? `Mengen als ${name}: ${sheets}, je mit Summenzeile. Räume sind nicht dabei.`
    : `Takeoff as ${name}: ${sheets}, each with a totals row. Rooms are left out.`;
}

const SHEET_DE: Record<string, string> = { Material: 'Material', Type: 'Typ', Storey: 'Geschoss' };

export function checkText(out: Record<string, unknown>, de: boolean): string {
  return findingsText((out.findings as Finding[] | undefined) ?? [], de);
}

export function findingsText(findings: Finding[], de: boolean): string {
  if (!findings.length) return de ? 'Keine Auffälligkeiten.' : 'Nothing to flag.';
  return findings.map((f) => findingText(f, de)).join(' ');
}

const FINDING_TEXT: Record<string, (f: Finding, de: boolean) => string> = {
  no_vertical_access: (f, de) =>
    de
      ? `${f.storey} hat keinen Treppen- oder Rampenzugang.`
      : `${f.storey} has no stair or ramp reaching it.`,
  roof_below_storey: (f, de) =>
    de ? `Das Dach liegt unter ${f.storey}.` : `The roof sits below ${f.storey}.`,
  no_roof: (_f, de) => (de ? 'Das Gebäude hat kein Dach.' : 'The building has no roof.'),
  storey_without_slab: (f, de) =>
    de ? `${f.storey} hat Wände, aber keine Decke.` : `${f.storey} has walls but no floor slab.`,
  walls_without_openings: (f, de) =>
    de
      ? `${f.storey}: keine Wand hat Tür oder Fenster.`
      : `${f.storey}: no wall carries a door or window.`,
  no_spaces: (f, de) =>
    de ? `${f.storey} hat Wände, aber keine Räume.` : `${f.storey} has walls but no rooms.`,
  unnamed_material: (f, de) =>
    de
      ? `${f.ids.length} neue Elemente ohne Material.`
      : `${f.ids.length} new elements carry no material.`,
};

function findingText(f: Finding, de: boolean): string {
  const text = FINDING_TEXT[f.code]?.(f, de) ?? f.message;
  return f.suggest ? text + (de ? ' Soll ich das beheben?' : ' Fix it?') : text;
}
